import {
  Box,
  Container,
  Stack,
  SvgIcon,
  Typography,
  styled
} from "@mui/material";
import {
  ArrowDoubleRight20Px,
  Bookmark20Px,
  ConfirmationModal,
  useToast
} from "@qubit/autoparts";
import { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import useFlag from "~/config/flags";
import { useFcNavigate } from "~/hooks/useFcNavigate";

import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import { FlaggedReasonType } from "~/lib/bin";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  selectThisWorkstation,
  selectWorkstationAutostoreGridId,
  selectWorkstationAvailablePorts
} from "~/redux/selectors/workstationsSelectors";

import {
  usePostSkipMaintenanceInventoryMovementMutation,
  useStartBinMaintenanceMutation
} from "~/redux/warehouse/autostoreBinMaintenance.hooks";
import { useCloseWorkstationMutation } from "~/redux/warehouse/workstation.hooks";
import { StartBinMaintenanceRequest, TemperatureZone } from "~/types/api";

import {
  BinMaintenanceModeModal,
  TwoPortBinMaintenanceMode
} from "./BinMaintenanceModeModal";
import { BinMaintenancePort } from "./BinMaintenancePort";

import { MaintenanceSessionConfigChip } from "./MaintenanceSessionConfigChip";
import { StartMaintenanceConfigModal } from "./StartMaintenanceConfigModal";
import {
  resetMaintenanceConfig,
  setIsMoveErrorModalOpen,
  setMovementData,
  setSourceDestinationBins,
  setTaskState
} from "./binMaintenanceWorkstation.slice";

interface BinMaintenanceWorkstation {
  viewTitle: ViewNameTranslation;
}

const ActionHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== "enableColorChanges"
})<{ enableColorChanges: boolean }>(({ theme, enableColorChanges }) => ({
  margin: `${theme.spacing(14)} auto`,
  padding: theme.spacing(2),
  backgroundColor: enableColorChanges
    ? theme.palette.autostoreDesignSystem.surface.static.informative
    : theme.palette.primary.light,
  color: enableColorChanges
    ? theme.palette.autostoreDesignSystem.text.onStandard.statusInformative
    : theme.palette.primary.main,
  width: "fit-content"
}));

export const BinMaintenanceWorkstation: React.FC<BinMaintenanceWorkstation> = (
  props
) => {
  const { viewTitle } = props;
  useNavbar({ viewTitle });
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { errorToast, successToast } = useToast();
  const { enableColorChanges, showMaintenanceConfigurationModal } = useFlag();
  const navigate = useFcNavigate();
  const trackedPageName = useMixpanelPageName();

  const selectedWorkstation = useAppSelector(selectThisWorkstation);
  const autostoreGridId = useAppSelector(selectWorkstationAutostoreGridId);
  const ports = useAppSelector(selectWorkstationAvailablePorts);
  const {
    movementTaskState,
    movementData,
    isMoveErrorModalOpen,
    maintenanceConfig
  } = useAppSelector((state) => state.binMaintenanceWorkstation);
  const sourceAndDestinationBin =
    movementData?.inventoryMovements?.sourceAndDestinationBin;

  const [twoPortMode, setTwoPortMode] =
    useState<TwoPortBinMaintenanceMode | null>(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);

  const isTwoPortWorkstation = selectedWorkstation?.ports.length === 2;
  const shouldSelectMode =
    isTwoPortWorkstation && !twoPortMode && !showMaintenanceConfigurationModal;

  let modeToSend: StartBinMaintenanceRequest["mode"] =
    isTwoPortWorkstation && !!twoPortMode ? twoPortMode : "mixed";
  if (showMaintenanceConfigurationModal) {
    modeToSend = undefined;
  }

  const [closeWorkstation] = useCloseWorkstationMutation();
  const [startBinMaintenance, { isLoading: isStartBinMaintenanceLoading }] =
    useStartBinMaintenanceMutation();
  const [skipInventoryMovement] =
    usePostSkipMaintenanceInventoryMovementMutation();

  const startInventoryMovements = useCallback(
    async (
      sessionFilters: {
        temperatureZone?: TemperatureZone;
        flaggedReasons?: FlaggedReasonType[];
      } = {}
    ) => {
      if (!autostoreGridId || !selectedWorkstation?.id || shouldSelectMode) {
        return;
      }

      // fallback to existing maintenance config if not explicitly provided
      const temperatureZone =
        sessionFilters.temperatureZone ??
        maintenanceConfig.tempZone ??
        undefined;
      const flaggedReasons =
        sessionFilters.flaggedReasons ?? maintenanceConfig.flaggedReasons;

      try {
        await closeWorkstation({
          autostoreGridId,
          workstationId: selectedWorkstation?.id
        }).unwrap();
        const startMovementData = await startBinMaintenance({
          autostoreGridId,
          workstationId: selectedWorkstation?.id,
          mode: modeToSend,
          ...(showMaintenanceConfigurationModal
            ? {
                flaggedReasons,
                ...(temperatureZone ? { temperatureZone } : {})
              }
            : {})
        }).unwrap();

        if (
          startMovementData.binCleaningPorts.length == 0 &&
          startMovementData.inventoryMovements.sourceAndDestinationBin === null
        ) {
          successToast(t("all bin maintenance tasks completed"));
        } else {
          dispatch(setMovementData(startMovementData));
          dispatch(
            setTaskState(
              startMovementData.inventoryMovements.sourceAndDestinationBin
                ?.sourceBin
                ? "MovingProduct"
                : "CleaningBin"
            )
          );
        }
      } catch (error) {
        errorToast(getMessageFromRtkError(error));
      }
    },
    [
      autostoreGridId,
      selectedWorkstation?.id,
      shouldSelectMode,
      showMaintenanceConfigurationModal,
      modeToSend,
      maintenanceConfig.tempZone,
      maintenanceConfig.flaggedReasons,
      startBinMaintenance,
      errorToast,
      successToast,
      dispatch,
      closeWorkstation,
      t
    ]
  );

  // initiate bin maintenance on landing (if no configuration required)
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (shouldSelectMode || showMaintenanceConfigurationModal) {
      return;
    }
    void startInventoryMovements();
  }, [
    startInventoryMovements,
    shouldSelectMode,
    showMaintenanceConfigurationModal
  ]);

  // if sourceAndDestinationBin is null, we may be out of movement tasks
  // call startInventoryMovements to get bins to open for cleaning
  useEffect(() => {
    if (
      sourceAndDestinationBin === null &&
      movementTaskState === "MovingProduct"
    ) {
      successToast(t("inventory movements complete"));
      void startInventoryMovements();
    }
    // intentionally not including startInventoryMovements as dependency to avoid re-triggering the effect when the config modal is opened
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceAndDestinationBin, movementTaskState, successToast, t]);

  // on unmount, reset the task state
  useEffect(
    () => () => {
      dispatch(setTaskState(null));
      dispatch(resetMaintenanceConfig());
    },
    [dispatch]
  );

  useEffect(() => {
    if (showMaintenanceConfigurationModal) {
      setIsConfigModalOpen(true);
    }
  }, [showMaintenanceConfigurationModal]);

  let headerText = t("begin inventory move");
  switch (movementTaskState) {
    case "CleaningBin":
      headerText = t("unflag bin");
      break;
    case "MovingProduct":
      headerText = t("begin inventory move");
      break;
    case "ConfirmingMovement":
      headerText = t("unflag bin");
      break;
  }

  return (
    <Container maxWidth="xl" sx={{ position: "relative" }}>
      <Stack direction="column" width="1360px" justifySelf="center">
        <ActionHeader enableColorChanges={enableColorChanges}>
          <Stack direction="row">
            <SvgIcon
              component={
                movementTaskState === "CleaningBin" ||
                movementTaskState === "ConfirmingMovement"
                  ? Bookmark20Px
                  : ArrowDoubleRight20Px
              }
              sx={{ fontSize: "48px", alignSelf: "end" }}
            />
            <Typography variant="h3">{headerText}</Typography>
          </Stack>
        </ActionHeader>

        <Stack direction="row" justifyContent="space-between" spacing={10}>
          {ports.map((portId) => {
            return (
              <BinMaintenancePort
                key={portId}
                portId={portId}
                isLoading={isStartBinMaintenanceLoading}
              />
            );
          })}
        </Stack>
      </Stack>
      {isMoveErrorModalOpen && autostoreGridId && selectedWorkstation && (
        <ConfirmationModal
          isOpen={isMoveErrorModalOpen}
          closeCb={() => {
            dispatch(setIsMoveErrorModalOpen(false));
            mixpanelTrack({
              type: "Button Click",
              label: "Close",
              trackedPageName: "Movement Error Modal"
            });
          }}
          modalTitle={t("movement error")}
          modalText={t(
            "please replace inventory into original bin navigate to the inventory page to resolve issues"
          )}
          noCancelButton
          confirmText={t("confirm")}
          confirmCb={async () => {
            const nextBins = await skipInventoryMovement({
              autostoreGridId,
              workstationId: selectedWorkstation.id
            }).unwrap();
            dispatch(
              setSourceDestinationBins({
                sourceAndDestinationBin: nextBins
              })
            );
            dispatch(setIsMoveErrorModalOpen(false));
            dispatch(setTaskState("MovingProduct"));
            mixpanelTrack({
              type: "Button Click",
              label: "Confirm",
              trackedPageName: "Movement Error Modal",
              eventProperties: {
                "Workstation Name": selectedWorkstation.deviceId,
                "Grid Name": selectedWorkstation.autostoreGridName
              }
            });
          }}
          labelledById="movement-error-modal-title"
        />
      )}
      <BinMaintenanceModeModal
        isOpen={shouldSelectMode}
        onClose={() => navigate(-1)}
        onConfirm={(mode) => setTwoPortMode(mode)}
      />
      {showMaintenanceConfigurationModal && (
        <StartMaintenanceConfigModal
          isOpen={isConfigModalOpen}
          onClose={() => {
            setIsConfigModalOpen(false);
          }}
          onConfirm={({ temperatureZone, flaggedReasons }) => {
            void startInventoryMovements({ temperatureZone, flaggedReasons });
          }}
        />
      )}
      <Stack position="fixed" bottom={40} right={40}>
        {showMaintenanceConfigurationModal && (
          <MaintenanceSessionConfigChip
            tempZone={maintenanceConfig.tempZone ?? undefined}
            flaggedReasons={maintenanceConfig.flaggedReasons}
            onEdit={() => {
              mixpanelTrack({
                type: "Button Click",
                label: "Edit Maintenance Config",
                trackedPageName
              });
              setIsConfigModalOpen(true);
            }}
          />
        )}
      </Stack>
    </Container>
  );
};
